import React from "react";

import { parseCss } from "@use/parse-html";

import { ParseOptions, ReactHtmlParser } from "../ReactHtmlParser";
import { getAriaAndDataAttributes } from "./getAriaAndDataAttributes";
import { getNamedAttribute } from "./getNamedAttribute";
import { getPropsFromAttributes } from "./getPropsFromAttributes";

/**
 * Simple utility that converts an html element into a React Node, based on the
 * element attribute maps defined in elementAttribuets.ts
 */
export function parseHtmlElement(
  elem: Element,
  parser: ReactHtmlParser,
  options: ParseOptions
): Promise<React.ReactNode | null> | React.ReactNode | null {
  const tagName = elem.tagName.toLowerCase();

  // Determine if this tag is allowed based on available black- or whitelist,
  // and if not then bail out
  if (!parser.isTagAllowed(tagName)) {
    // On non-production environments we'll warn developers that the tag is
    // not allowed
    if (process.env.NODE_ENV !== "production" && options.quiet !== true) {
      console.warn(
        "parseHtmlElement(): " + `The element "${tagName}" is not allowed.`
      );
    }

    return null;
  }

  // If we get here, then the tag is allowed... Begin translating the required
  // props from the DOM node to the React node
  const props: {
    [key: string]:
      | string
      | number
      | boolean
      | { [key: string]: string }
      | undefined;
  } = {
    // Make sure that a key is assigned to the node to surpress warnings
    // from React
    key: getNamedAttribute(elem, "key"),

    // Convert inline css into a style object that can be parsed properly by
    // React
    style:
      parser.isAttrAllowed("style") && elem.hasAttribute("style")
        ? parseCss(String(getNamedAttribute(elem, "style")))
        : undefined,

    // Transform all given attributes from the HTML feed into their
    // corresponding React props
    ...getPropsFromAttributes(elem, parser),

    // ... And forward all given data-* and aria-* attributes directly to
    // React as is
    ...getAriaAndDataAttributes(elem),
  };

  // Parse out children
  const children =
    options.sync !== true
      ? parseHtmlChildren(elem, parser, options)
      : parseHtmlChildrenSync(elem, parser, options);

  // If we're not running in strictly synchroneous mode, then wait for any
  // child promises before resolving the external promise
  if (options.sync !== true) {
    return new Promise(async (resolve, reject) => {
      try {
        resolve(React.createElement(tagName, props, await children));
      } catch (err) {
        reject(err);
      }
    });
  }

  if (children instanceof Promise) {
    throw new Error("parseHtmlElement(): get async children, but sync: true was given");
  }

  // ... Otherwise return the result "as is"
  return React.createElement(tagName, props, children);
}

/**
 * Helper that'll parse all children of an html element, automatically handling
 * any promises returned by the parsers.
 */
export async function parseHtmlChildren(
  elem: Element,
  parser: ReactHtmlParser,
  options: ParseOptions
): Promise<React.ReactNode[] | null> {
  switch (elem.tagName.toLowerCase()) {
    case "area":
    case "base":
    case "br":
    case "col":
    case "embed":
    case "hr":
    case "img":
    case "input":
    case "keygen":
    case "link":
    case "menuitem":
    case "meta":
    case "param":
    case "source":
    case "track":
    case "wbr":
      // The above tags do NOT allow children at all, so don't parse
      // anything what so ever...
      return null;

    case "table":
    case "thead":
    case "tbody":
    case "tfoot":
    case "tr":
      // The above tags do NOT allow whitespace text nodes
      return (await parser.parseChildren(elem, options)).filter(
        (child) => typeof child !== "string" || child.match(/\S/)
      );

    default:
      return parser.parseChildren(elem, options);
  }
}

/**
 * Helper that'll parse all children of an html element, ignoring the output of
 * any parsers that return promises.
 */
export function parseHtmlChildrenSync(
  elem: Element,
  parser: ReactHtmlParser,
  options: ParseOptions
): React.ReactNode[] | null {
  switch (elem.tagName.toLowerCase()) {
    case "area":
    case "base":
    case "br":
    case "col":
    case "embed":
    case "hr":
    case "img":
    case "input":
    case "keygen":
    case "link":
    case "menuitem":
    case "meta":
    case "param":
    case "source":
    case "track":
    case "wbr":
      // The above tags do NOT allow children at all, so don't parse
      // anything what so ever...
      return null;

    case "table":
    case "thead":
    case "tbody":
    case "tfoot":
    case "tr":
      // The above tags do NOT allow whitespace text nodes
      return parser
        .parseChildrenSync(elem, options)
        .filter((child) => typeof child !== "string" || child.match(/\S/));

    default:
      return parser.parseChildrenSync(elem, options);
  }
}
